import {Stack, Typography, Chip} from "@mui/material";
import {isEmpty} from "../../config/commonFunctions";
import {keys} from "../../config/nameSpacesKeys";
import MsLanguageResourceItemDisplay from "./MsLanguageResourceItemDisplay";
import ExpandableText from "./ExpandableText";
import EnvironmentalImpactDisplay from "./EnvironmentalImpactDisplay";
import EvaluationDisplay from "./EvaluationDisplay";
import {MarkdownSection} from "../utils/landingPageSections";
import {useTranslator} from "../../context/TranslatorContext";

export default function ModelPropertiesDisplay(props) {
  const {
    mappings, defaultLanguage,
    // core
    modelType, variantOf, bias, limitations, contextLength,
    quantized, quantizationProcess, parameterPrecision, trainedOn, finetuneDataset,
    creationDetails, evaluationDataset, evaluationResults, baseModel,
    // extended (v0_9_5) — undefined when not provided
    instructions, designSpecifications, requiresSoftware, finetuningMethodology, finetuningDescription,
    evaluation, hasInferencingImpact, hasTrainingImpact, normalizeBreaks, finetuningHardware,
  } = props;
  const regex = /(<([^>]+)>)/gi;
  const {messages} = useTranslator();

  function getLabel(key, defaultValue) {
    const returnValue = mappings[key] || defaultValue;
    if (!returnValue) {
      return key;
    }
    return returnValue;
  }
  return (
    <> {/* <Paper variant="outlined"> */}
      <Stack direction="column" alignItems="flex-start" justifyContent="flex-start">
        <Stack sx={{pt: 1, pb: 1}} spacing={1}>
          {!isEmpty(getLabel(modelType.getValue())) && (
            <div>
              <Typography variant="body2" className={"key"}>
                {getLabel(keys.modelType, "modelType")}
              </Typography>
              <Typography variant="body1">{getLabel(modelType.getValue())}</Typography>
            </div>
          )}

          <MsLanguageResourceItemDisplay defaultLanguage={defaultLanguage} mappings={mappings}
            topLabel={getLabel(keys.variantOf, "variantOf")}
            msLanguageResourceList={variantOf.getObjList()} normalizeBreaks={normalizeBreaks}/>

          {evaluation && evaluation.getObjList().length > 0 && (
            <EvaluationDisplay
              objList={evaluation.getObjList()}
              defaultLanguage={defaultLanguage}
              mappings={mappings}
              normalizeBreaks={normalizeBreaks}
              topLabel={getLabel(keys.evaluation, "evaluation")}
            />
          )}

          {hasInferencingImpact && hasInferencingImpact.getObjList().length > 0 && (
            <EnvironmentalImpactDisplay
              objList={hasInferencingImpact.getObjList()}
              defaultLanguage={defaultLanguage}
              mappings={mappings}
            />
          )}

          {hasTrainingImpact && hasTrainingImpact.getObjList().length > 0 && (
            <EnvironmentalImpactDisplay
              objList={hasTrainingImpact.getObjList()}
              defaultLanguage={defaultLanguage}
              mappings={mappings}
            />
          )}

          {!isEmpty(getLabel(bias.getValue(defaultLanguage))) && (
            <MarkdownSection
              field={bias}
              defaultLanguage={defaultLanguage}
              getLabel={getLabel}
              normalizeBreaks={normalizeBreaks}
              labelKey={keys.bias}
              fallbackLabel="bias"
            />
          )}

          {!isEmpty(getLabel(limitations.getValue(defaultLanguage))) && (
            <MarkdownSection
              field={limitations}
              defaultLanguage={defaultLanguage}
              getLabel={getLabel}
              normalizeBreaks={normalizeBreaks}
              labelKey={keys.limitations}
              fallbackLabel="limitations"
            />
          )}

          {instructions && !isEmpty(instructions.getValue(defaultLanguage)) && (
            <MarkdownSection
              field={instructions}
              defaultLanguage={defaultLanguage}
              getLabel={getLabel}
              normalizeBreaks={normalizeBreaks}
              labelKey={keys.instructions}
              fallbackLabel="instructions"
            />
          )}

          {designSpecifications && !isEmpty(designSpecifications.getValue(defaultLanguage)) && (
            <MarkdownSection
              field={designSpecifications}
              defaultLanguage={defaultLanguage}
              getLabel={getLabel}
              normalizeBreaks={normalizeBreaks}
              labelKey={keys.designSpecifications}
              fallbackLabel="designSpecifications"
            />
          )}

          {requiresSoftware && !isEmpty(requiresSoftware.getValue(defaultLanguage)) && (
            <div style={{paddingTop: "0.5em"}}>
              <Typography variant="body2" className="key">
                {getLabel(keys.requiresSoftware, "requiresSoftware")}
              </Typography>
              <ExpandableText text={requiresSoftware.getValue(defaultLanguage).replace(regex, "")} />
            </div>
          )}

          {finetuningMethodology && !isEmpty(finetuningMethodology.getValue(defaultLanguage)) && (
            <MarkdownSection
              field={finetuningMethodology}
              defaultLanguage={defaultLanguage}
              getLabel={getLabel}
              normalizeBreaks={normalizeBreaks}
              labelKey={keys.finetuningMethodology}
              fallbackLabel="finetuningMethodology"
            />
          )}

          {finetuningDescription && !isEmpty(finetuningDescription.getValue(defaultLanguage)) && (
            <MarkdownSection
              field={finetuningDescription}
              defaultLanguage={defaultLanguage}
              getLabel={getLabel}
              normalizeBreaks={normalizeBreaks}
              labelKey={keys.finetuningDescription}
              fallbackLabel="finetuningDescription"
            />
          )}

          {finetuningHardware && !isEmpty(finetuningHardware.getValue()) && (<div style={{ paddingTop: "8px" }}>
            <Typography variant="body2" className={"key"}>
              {getLabel(keys.finetuningHardware, "finetuningHardware")}
            </Typography>
            <Typography variant="body1" className={"key"}>
              {finetuningHardware.getValue()}
            </Typography>
          </div>
          )}
          {!isEmpty(contextLength.getValue()) && (
            <div>
              <Typography variant="body2" className={"key"}>
                {getLabel(keys.contextLength, "contextLength")}
              </Typography>
              <Typography variant="body1">
                {contextLength.getValue()}
              </Typography>
            </div>
          )}

          {!isEmpty(quantized.getValue()) && (
            <div>
              <Typography variant="body1" className={"key"}>
                {getLabel(keys.quantized, "quantized")}
              </Typography>
              <Chip label={quantized.getValue() ? messages.yes_message : messages.no_message} sx={{fontWeight: "600"}} />
            </div>
          )}

          {!isEmpty(getLabel(quantizationProcess.getValue())) && (
            <div style={{paddingTop: "0.5em"}}>
              <Typography variant="body2" className={"key"}>
                {getLabel(keys.quantizationProcess, "quantizationProcess")}
              </Typography>
              <Typography variant="body1" style={{textAlign: "justify"}}>
                {quantizationProcess.getValue(quantizationProcess).replace(regex, "")}
              </Typography>
            </div>)}

          {!parameterPrecision.isEmpty() && (
            <div>
              <Typography variant="body2" className={"key"}>
                {getLabel(keys.parameterPrecision, "parameterPrecision")}
              </Typography>
              <Typography variant="body1">
                {parameterPrecision.getCurrentValue()}
              </Typography>
            </div>
          )}

          <MsLanguageResourceItemDisplay defaultLanguage={defaultLanguage} mappings={mappings}
            topLabel={getLabel(keys.trainedOn, "trainedOn")}
            msLanguageResourceList={trainedOn.getObjList()} normalizeBreaks={normalizeBreaks}/>

          <MsLanguageResourceItemDisplay defaultLanguage={defaultLanguage} mappings={mappings}
            topLabel={getLabel(keys.finetuneDataset, "finetuneDataset")}
            msLanguageResourceList={finetuneDataset.getObjList()} normalizeBreaks={normalizeBreaks}/>


          {!isEmpty(getLabel(creationDetails.getValue(defaultLanguage))) && (
            <div style={{paddingTop: "0.5em"}}>
              <Typography variant="body2" className={"key"}>
                {getLabel(keys.creationDetails, "creationDetails")}
              </Typography>
              <ExpandableText text={creationDetails.getValue(defaultLanguage).replace(regex, "")} />
            </div>)}

          <MsLanguageResourceItemDisplay defaultLanguage={defaultLanguage} mappings={mappings}
            topLabel={getLabel(keys.evaluationDataset, "evaluationDataset")}
            msLanguageResourceList={evaluationDataset.getObjList()} normalizeBreaks={normalizeBreaks}/>


          {!isEmpty(evaluationResults.getValue(defaultLanguage)) && (
            <MarkdownSection
              field={evaluationResults}
              defaultLanguage={defaultLanguage}
              getLabel={getLabel}
              normalizeBreaks={normalizeBreaks}
              labelKey={keys.evaluationResults}
              fallbackLabel="evaluationResults"
            />
          )}

          {baseModel && !baseModel.isEmpty() && <MsLanguageResourceItemDisplay defaultLanguage={defaultLanguage} mappings={mappings}
            topLabel={getLabel(keys.baseModel, "baseModel")}
            msLanguageResourceList={baseModel.getObjList()} normalizeBreaks={normalizeBreaks}/>}
        </Stack>
      </Stack>
    </>
  );
}
